/**
 * Neue Mitarbeitende anlegen, einzeln (Stammdaten) oder gesammelt (neue Namen
 * aus dem Stundenimport). Alle oder keiner: die API legt sie in einem Rutsch an.
 *
 * Die IBAN ist freiwillig, weil ein neuer Name aus Gastromatic sie selten schon
 * mitbringt. Wer sie leer lässt, liest direkt daneben, was das bedeutet — und
 * die Person steht danach in der Mitarbeiterliste oben, bis sie eingetragen ist.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { ErrorBox, Info } from "./ui";

export interface NewEmployeeRow {
  personalNr: string;
  firstName: string;
  lastName: string;
  timeClockId: string;
  iban: string;
}

export function emptyRow(): NewEmployeeRow {
  return { personalNr: "", firstName: "", lastName: "", timeClockId: "", iban: "" };
}

const toInt = (v: string): number | null => (/^\d+$/.test(v.trim()) ? Number(v.trim()) : null);

export function NewEmployees({
  initial,
  fixedPersonalNr = false,
  submitLabel,
  onCreated,
  onCancel,
}: {
  initial: NewEmployeeRow[];
  /** Im Import steht die Personalnummer fest; sie kommt aus Gastromatic. */
  fixedPersonalNr?: boolean;
  submitLabel: string;
  onCreated: (created: { id: string; hasIban: boolean }[]) => void;
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<NewEmployeeRow[]>(initial);

  // Vorschlag für die erste leere Stempeluhr-ID. Weitere Zeilen zählen hoch;
  // die API prüft trotzdem, ob eine vergeben ist.
  const next = useQuery({
    queryKey: ["employees", "next-time-clock-id"],
    queryFn: () => api.get<{ timeClockId: number | null }>("/employees/next-time-clock-id"),
    enabled: initial.some((r) => !r.timeClockId),
  });
  const vorschlag = (index: number): string => {
    const start = next.data?.timeClockId;
    if (!start) return "";
    const offset = rows.slice(0, index).filter((r) => !r.timeClockId).length;
    return String(start + offset);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<{ created: { id: string; hasIban: boolean }[] }>("/employees/batch", {
        employees: rows.map((r, i) => ({
          personalNr: toInt(r.personalNr),
          firstName: r.firstName.trim(),
          lastName: r.lastName.trim(),
          timeClockId: toInt(r.timeClockId || vorschlag(i)),
          iban: r.iban.trim() || null,
        })),
      }),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ["employees"] });
      onCreated(res.created);
    },
  });

  const set = (i: number, patch: Partial<NewEmployeeRow>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const complete = rows.every((r) => toInt(r.personalNr) !== null && r.lastName.trim().length > 0);
  const ohneIban = rows.filter((r) => !r.iban.trim()).length;

  return (
    <div className="card p-5">
      <div className="space-y-5">
        {rows.map((r, i) => (
          <div key={i} className={i > 0 ? "border-t border-line pt-5" : ""}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
              <label className="block">
                <span className="label mb-1 block">PersNr</span>
                <input
                  className="field font-mono"
                  inputMode="numeric"
                  value={r.personalNr}
                  readOnly={fixedPersonalNr}
                  onChange={(e) => set(i, { personalNr: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="label mb-1 block">Vorname</span>
                <input className="field" value={r.firstName} onChange={(e) => set(i, { firstName: e.target.value })} />
              </label>
              <label className="block">
                <span className="label mb-1 block">Nachname</span>
                <input className="field" value={r.lastName} onChange={(e) => set(i, { lastName: e.target.value })} />
              </label>
              <label className="block">
                <span className="label mb-1 flex items-center">
                  Stempeluhr-ID
                  <Info text="Steht statt des Namens auf dem Aushang. Vorgeschlagen ist die nächste freie Nummer; sie muss zur Stempeluhr passen." />
                </span>
                <input
                  className="field font-mono"
                  inputMode="numeric"
                  placeholder={vorschlag(i)}
                  value={r.timeClockId}
                  onChange={(e) => set(i, { timeClockId: e.target.value })}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="label mb-1 block">IBAN</span>
                <input
                  className="field font-mono"
                  placeholder="DE.."
                  autoComplete="off"
                  spellCheck={false}
                  value={r.iban}
                  onChange={(e) => set(i, { iban: e.target.value })}
                />
                {!r.iban.trim() ? (
                  <span className="mt-1 block text-xs text-warn">
                    Ohne IBAN kein Geld: die Person steht dann unter „IBAN fehlt“, bis sie eingetragen ist.
                  </span>
                ) : null}
              </label>
            </div>
          </div>
        ))}
      </div>

      {create.error ? (
        <div className="mt-4">
          <ErrorBox error={create.error} />
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button className="btn-primary" disabled={!complete || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? "Legt an …" : submitLabel}
        </button>
        {onCancel ? (
          <button className="btn-ghost" onClick={onCancel}>
            Abbrechen
          </button>
        ) : null}
        {ohneIban > 0 && complete ? (
          <span className="text-xs text-muted">
            {ohneIban === 1 ? "Eine Person" : `${ohneIban} Personen`} ohne IBAN. Das geht, die Auszahlung
            wartet dann darauf.
          </span>
        ) : null}
      </div>
    </div>
  );
}
